import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { createFailureMessage, getHardLimitErrorFields, isUnconfirmedWrite } from '@/lib/apiErrors';
import { phDateTimeLocal, phLocalToIso } from '@/lib/phDate';
import { parseTypedAmount } from '@/lib/number';
import { PAIR_EXAMPLE, PAIR_HINT, formatPair, parsePairInput, type PairSide } from '@/lib/pairs';
import { isPairSecond, paramByKey, refusedValueMessage, type ParamDef } from '@/lib/waterModel';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import type { Crop } from '@/lib/crop';
import type { WaterCheckResponse, WaterCheckUpdate } from '@/types/generated/openapi';
import { CHECK_PARAMS } from './waterChecks';

const INTEGER_COLUMNS = new Set(['transparency_cm', 'alkalinity_ppm', 'alkalinity_2', 'calcium_ppm', 'magnesium_ppm']);
// Mirrors WaterCheckCreate (backend/app/schemas/water_check.py): a value over the cap is a 422 with no per-field text.
const MAX_BY_COLUMN: Record<string, number> = { ph: 14, temperature_c: 50, salinity_ppt: 50 };
// The server refuses a depth, transparency, calcium or magnesium of exactly 0 (a missed measurement, not a reading).
const ZERO_IS_INVALID = new Set(['depth_m', 'transparency_cm', 'calcium_ppm', 'magnesium_ppm']);
const COLOR_OPTIONS = paramByKey('waterColor')?.options ?? [];

export type WaterCheckValues = Omit<WaterCheckUpdate, 'measured_at' | 'technician_id'>;

interface Props {
  crop: Crop;
  initial?: WaterCheckResponse;
  /** Phone layout: 48px targets, 16px text so iOS doesn't zoom on focus. */
  phone?: boolean;
  busy: boolean;
  /** Offline: the save is queued and goes out on reconnect, so the form can be closed. */
  paused?: boolean;
  error: unknown;
  submitLabel: string;
  /** `measuredAtChanged` is false for an edit that left the time alone: send values only, so seconds and an out-of-prep time survive. */
  onSubmit: (v: { measured_at: string; measuredAtChanged: boolean; values: WaterCheckValues }) => void;
  onCancel: () => void;
}

const savedNumber = (initial: WaterCheckResponse | undefined, column: string): number | null => {
  const v = (initial as unknown as Record<string, string | number | null | undefined> | undefined)?.[column];
  return v === null || v === undefined || v === '' ? null : Number(v);
};

/** A pair's cell text ("100/110") from the saved check. */
function initialPairText(initial: WaterCheckResponse | undefined, p: ParamDef): string {
  const second = CHECK_PARAMS.find((x) => x.key === p.pairSecondKey)!;
  return formatPair(savedNumber(initial, p.dbColumn), savedNumber(initial, second.dbColumn)) ?? '';
}

function initialText(initial: WaterCheckResponse | undefined, column: string, numeric: boolean): string {
  const v = (initial as unknown as Record<string, string | number | null | undefined> | undefined)?.[column];
  if (v === null || v === undefined) return '';
  return numeric ? String(Number(v)) : String(v);
}

/** The form body shared by the desktop dialog and the phone screen. The caller owns the mutation. */
export function WaterCheckForm({ crop, initial, phone, busy, paused, error, submitLabel, onSubmit, onCancel }: Props) {
  // The caller's mutation flips `busy` a tick after mutate(), so two clicks in one frame would both send.
  // Only a failure unlocks it again: after a success the dialog is still fading out with Save enabled.
  // A create that timed out may have committed, so it stays locked; an edit (PUT) is safe to resend.
  const { table } = useFarmThresholds();
  const unconfirmed = !initial && error != null && isUnconfirmedWrite(error);
  const sendingRef = useRef(false);
  useEffect(() => {
    if (!busy && error && !unconfirmed) sendingRef.current = false;
  }, [busy, error, unconfirmed]);
  const initialWhen = initial ? phDateTimeLocal(new Date(initial.measured_at)) : null;
  const [when, setWhen] = useState(() => initialWhen ?? phDateTimeLocal());
  const whenChanged = when !== initialWhen;
  const [staleError, setStaleError] = useState<unknown>(null);
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries([
      ...CHECK_PARAMS.map((p) => [
        p.dbColumn,
        p.pairSecondKey ? initialPairText(initial, p) : initialText(initial, p.dbColumn, p.type === 'number'),
      ]),
      ['water_color', initialText(initial, 'water_color', false)],
      ['notes', initialText(initial, 'notes', false)],
    ])
  );
  const [clearToBottom, setClearToBottom] = useState(() => initial?.transparency_clear_to_bottom === true);
  const set = (column: string, v: string) => {
    setStaleError(error);
    setText((t) => ({ ...t, [column]: v }));
  };

  const bad = new Map<string, string>();
  const values: Record<string, number | string | boolean | null> = {};
  for (const p of CHECK_PARAMS) {
    if (clearToBottom && p.dbColumn === 'transparency_cm') {
      values[p.dbColumn] = null;
      continue;
    }
    if (isPairSecond(p.key)) continue; // lives in its first half's cell
    const raw = text[p.dbColumn].trim();
    if (p.pairSecondKey) {
      const second = CHECK_PARAMS.find((x) => x.key === p.pairSecondKey)!;
      const input = parsePairInput(raw);
      if ('error' in input) {
        bad.set(p.dbColumn, `${p.label}: ${input.error}`);
      } else {
        // The form is the whole check: a side left blank keeps what was saved (nothing, on a new check).
        const side = (typed: PairSide, column: string) => (typed === 'clear' ? null : typed ?? savedNumber(initial, column));
        values[p.dbColumn] = side(input.first, p.dbColumn);
        values[second.dbColumn] = side(input.second, second.dbColumn);
        if ([p.dbColumn, second.dbColumn].some((c) => ZERO_IS_INVALID.has(c) && values[c] === 0)) {
          bad.set(p.dbColumn, `${p.label}: each number must be more than 0`);
        }
      }
      continue;
    }
    if (p.type === 'select' || raw === '') {
      values[p.dbColumn] = raw === '' ? null : raw;
      continue;
    }
    // The shared parser's wording ("Enter a number, like 7.5"), prefixed with the field.
    const parsed = parseTypedAmount(raw);
    const max = MAX_BY_COLUMN[p.dbColumn];
    if ('error' in parsed) {
      bad.set(p.dbColumn, `${p.label}: ${parsed.error}`);
      continue;
    }
    const n = parsed.value as number;
    if (n < 0) bad.set(p.dbColumn, `${p.label} can't be negative`);
    else if (INTEGER_COLUMNS.has(p.dbColumn) && !Number.isInteger(n)) bad.set(p.dbColumn, `${p.label} must be a whole number`);
    else if (ZERO_IS_INVALID.has(p.dbColumn) && n === 0) bad.set(p.dbColumn, refusedValueMessage(table, p.key, '0'));
    else if (max !== undefined && n > max) bad.set(p.dbColumn, `${p.label} can't be more than ${max}`);
    else values[p.dbColumn] = n;
  }
  values.transparency_clear_to_bottom = clearToBottom ? true : null;
  values.water_color = text.water_color || null;
  values.notes = text.notes.trim() || null;

  // The first refused field comes into view (its message sits right under it).
  const firstBadColumn = [...bad.keys()][0];
  useEffect(() => {
    if (firstBadColumn) document.getElementById(`wc-err-${firstBadColumn}`)?.scrollIntoView({ block: 'nearest' });
  }, [firstBadColumn]);

  const hasValue = clearToBottom || CHECK_PARAMS.some((p) => values[p.dbColumn] !== null && !bad.has(p.dbColumn));
  const tooEarly = whenChanged && !!crop.prep_start_date && when < `${crop.prep_start_date}T00:00`;
  const inFuture = whenChanged && when > phDateTimeLocal();
  const canSave = !!when && hasValue && bad.size === 0 && !tooEarly && !inFuture && !busy && !unconfirmed;
  // A server error describes the values as last submitted: drop it once the user edits anything.
  const liveError = error === staleError ? null : error;
  const rejected = new Set(getHardLimitErrorFields(liveError));
  const isRejected = (p: ParamDef) =>
    rejected.has(p.dbColumn) || (!!p.pairSecondKey && rejected.has(paramByKey(p.pairSecondKey)!.dbColumn));
  const rejectedMessages = CHECK_PARAMS.filter((p) => !isPairSecond(p.key) && isRejected(p)).map((p) =>
    refusedValueMessage(table, p.key, text[p.dbColumn].trim())
  );

  const field = cn('w-full rounded-lg border border-gray-300 px-3', phone ? 'h-12 text-base' : 'h-10 text-sm');
  const label = cn('flex flex-col gap-1.5 font-semibold text-gray-700', phone ? 'text-sm' : 'text-[13px]');
  // Each cell spans three shared rows (label, input, error) so inputs line up even when a label wraps.
  const gridLabel = cn('row-span-3 grid grid-rows-subgrid font-semibold text-gray-700', phone ? 'text-sm' : 'text-[13px]');

  return (
    <form
      className="flex min-h-0 flex-1 flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave || sendingRef.current) return;
        sendingRef.current = true;
        onSubmit({ measured_at: phLocalToIso(when), measuredAtChanged: whenChanged, values: values as WaterCheckValues });
      }}
    >
      <div className={cn('flex min-h-0 flex-1 flex-col gap-4', phone && 'overflow-y-auto overscroll-contain')}>
        <label className={label}>
          When (Philippine time)
          <input
            type="datetime-local"
            required
            className={field}
            value={when}
            // An untouched time on an older check (water-in moved later) must not fail native validation.
            min={crop.prep_start_date && (!initial || whenChanged) ? `${crop.prep_start_date}T00:00` : undefined}
            max={phDateTimeLocal()}
            onChange={(e) => {
              setStaleError(error);
              setWhen(e.target.value);
            }}
          />
          {tooEarly && <span className="text-xs font-normal text-red-700">Before this crop's water-in.</span>}
          {inFuture && <span className="text-xs font-normal text-red-700">That time hasn't happened yet.</span>}
        </label>
        {/* Cells inherit this row gap; between cells it counts twice (around the empty error row), matching the old gap-y-3. */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {CHECK_PARAMS.filter((p) => !isPairSecond(p.key)).map((p) => (
            <label key={p.key} className={gridLabel}>
              <span className="self-end">
                {p.label}
                {p.unit && <span className="font-normal text-gray-500"> {p.unit}</span>}
              </span>
              {p.type === 'select' ? (
                <select className={field} value={text[p.dbColumn]} onChange={(e) => set(p.dbColumn, e.target.value)}>
                  <option value="">—</option>
                  {p.options?.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  inputMode={p.pairSecondKey ? 'text' : INTEGER_COLUMNS.has(p.dbColumn) ? 'numeric' : 'decimal'}
                  placeholder={p.pairSecondKey ? PAIR_EXAMPLE[p.key] : undefined}
                  autoComplete="off"
                  aria-label={p.label}
                  aria-invalid={bad.has(p.dbColumn) || isRejected(p) || undefined}
                  aria-describedby={bad.has(p.dbColumn) ? `wc-err-${p.dbColumn}` : undefined}
                  className={cn(field, 'tabular-nums', (bad.has(p.dbColumn) || isRejected(p)) && 'border-red-500')}
                  value={clearToBottom && p.dbColumn === 'transparency_cm' ? '' : text[p.dbColumn]}
                  disabled={clearToBottom && p.dbColumn === 'transparency_cm'}
                  onChange={(e) => set(p.dbColumn, e.target.value)}
                />
              )}
              {bad.has(p.dbColumn) ? (
                <span id={`wc-err-${p.dbColumn}`} className="text-xs font-normal text-red-700">
                  {bad.get(p.dbColumn)}
                </span>
              ) : (
                p.pairSecondKey && <span className="text-xs font-normal text-gray-500">{PAIR_HINT[p.key]}</span>
              )}
            </label>
          ))}
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={clearToBottom}
            onChange={(e) => {
              setStaleError(error);
              setClearToBottom(e.target.checked);
            }}
            className="h-5 w-5"
          />
          Transparency: clear to bottom
        </label>
        <label className={label}>
          Water color
          <select className={field} value={text.water_color} onChange={(e) => set('water_color', e.target.value)}>
            <option value="">—</option>
            {COLOR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className={label}>
          Notes
          <input className={field} value={text.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional" />
        </label>
      </div>
      {bad.size > 0 && (
        <p role="alert" className="shrink-0 text-sm text-red-700">
          {[...bad.values()][0]}
          {bad.size > 1 ? ` (and ${bad.size - 1} more below)` : ''}
        </p>
      )}
      {!hasValue && bad.size === 0 && <p className="shrink-0 text-sm text-gray-500">Enter at least one measurement.</p>}
      {!unconfirmed && liveError != null && (
        <p role="alert" className="shrink-0 text-sm text-red-700">
          {rejectedMessages.length > 0
            ? `Not saved. ${rejectedMessages.join(' ')}`
            : isUnconfirmedWrite(liveError)
              ? 'Not saved: no answer from the server, try again.'
              : `Not saved: ${liveError instanceof Error ? liveError.message : 'try again'}`}
        </p>
      )}
      {paused && (
        <p role="status" className="shrink-0 text-sm text-amber-700">
          No connection. It will save when you are back online, even if you close this.
        </p>
      )}
      {unconfirmed && (
        <p role="alert" className="shrink-0 text-sm text-red-700">
          {createFailureMessage(error, '')}
        </p>
      )}
      <div className={cn('flex justify-end gap-2', phone && 'shrink-0')}>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy && !paused}
          className={cn(
            'rounded-lg border border-gray-300 bg-white px-3.5 font-semibold text-gray-900 disabled:opacity-50',
            phone ? 'min-h-[48px] text-base' : 'min-h-[40px] text-sm'
          )}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className={cn(
            'rounded-lg bg-primary-600 px-3.5 font-semibold text-white disabled:opacity-50',
            phone ? 'min-h-[48px] text-base' : 'min-h-[40px] text-sm'
          )}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
